import type {
  ApmReporter,
  JsonObject,
  ReportingEnvironment,
} from '@meitu/hubble-channel';

const WEB_APM_APP_NAME = 'RoboNeo-web';
const WEB_APM_REPORT_TYPE = 'h5_network_metric';
const WEB_APM_SDK_VERSION = '1.0.0';

type WebApmReporter = Pick<ApmReporter, 'flush' | 'reportNow' | 'stop'>;

interface WebApmRuntimeConfig {
  appKey: string;
  environment: ReportingEnvironment;
}

interface WebApmNavigationTiming {
  timestamp: number;
  redirectStart: number;
  redirectEnd: number;
  fetchStart: number;
  domainLookupStart: number;
  domainLookupEnd: number;
  connectStart: number;
  connectEnd: number;
  requestStart: number;
  responseStart: number;
  responseEnd: number;
  unloadEventStart: number;
  unloadEventEnd: number;
  domLoading: number;
  domInteractive: number;
  domContentLoadedEventEnd: number;
  loadEventEnd: number;
}

export interface WebApmPerformanceOptions {
  fmp?: number;
  fmi?: number;
  errors?: JsonObject[];
}

type ExtendedPerformanceResourceTiming = PerformanceResourceTiming & {
  contentType?: string;
  responseStatus?: number;
};

let reporter: WebApmReporter | null = null;
let reporterPromise: Promise<WebApmReporter | null> | null = null;

/**
 * 输出不包含配置值的降级日志，避免 APM 故障影响页面主流程或泄露凭据。
 */
function warnWebApmFailure(message: string, error?: unknown): void {
  if (error) {
    console.warn(`[Web APM] ${message}`, error);
    return;
  }
  console.warn(`[Web APM] ${message}`);
}

/**
 * 将浏览器性能时间统一为非负毫秒数，并保留三位小数。
 */
function normalizeWebApmDuration(value: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }
  return Math.round(value * 1000) / 1000;
}

/**
 * 计算两个性能时间点之间的非负耗时。
 */
function getWebApmDuration(end: number, start: number): number {
  if (end <= 0 || start < 0 || end < start) {
    return 0;
  }
  return normalizeWebApmDuration(end - start);
}

/**
 * 把旧版 PerformanceTiming 的绝对时间转换为相对 navigationStart 的时间。
 */
function getLegacyRelativeTime(value: number, navigationStart: number): number {
  return value > 0 && navigationStart > 0 ? Math.max(0, value - navigationStart) : 0;
}

/**
 * Navigation Timing Level 2 缺失有效分段时回退旧版 Timing，保留连接复用时真实的 0。
 */
function resolveWebApmTimingPair(
  start: number,
  end: number,
  legacyStart: number,
  legacyEnd: number,
  legacyNavigationStart: number,
): { start: number; end: number; } {
  const legacyPair = {
    start: getLegacyRelativeTime(legacyStart, legacyNavigationStart),
    end: getLegacyRelativeTime(legacyEnd, legacyNavigationStart),
  };
  if (getWebApmDuration(end, start) > 0 || getWebApmDuration(legacyPair.end, legacyPair.start) === 0) {
    return { start, end };
  }
  return legacyPair;
}

/**
 * 优先读取 Navigation Timing Level 2，并兼容仍只提供 PerformanceTiming 的浏览器。
 */
function getWebApmNavigationTiming(): WebApmNavigationTiming | null {
  if (typeof performance === 'undefined') {
    return null;
  }

  const legacyTiming = performance.timing;
  const legacyNavigationStart = legacyTiming?.navigationStart ?? 0;
  const navigationEntry = performance.getEntriesByType?.('navigation')[0] as PerformanceNavigationTiming | undefined;

  if (navigationEntry) {
    const timeOrigin = performance.timeOrigin
      || legacyNavigationStart
      || Date.now() - performance.now();
    const legacyDomLoading = getLegacyRelativeTime(
      legacyTiming?.domLoading ?? 0,
      legacyNavigationStart,
    );
    const dnsTiming = resolveWebApmTimingPair(
      navigationEntry.domainLookupStart,
      navigationEntry.domainLookupEnd,
      legacyTiming?.domainLookupStart ?? 0,
      legacyTiming?.domainLookupEnd ?? 0,
      legacyNavigationStart,
    );
    const tcpTiming = resolveWebApmTimingPair(
      navigationEntry.connectStart,
      navigationEntry.connectEnd,
      legacyTiming?.connectStart ?? 0,
      legacyTiming?.connectEnd ?? 0,
      legacyNavigationStart,
    );

    return {
      timestamp: Math.round(timeOrigin),
      redirectStart: navigationEntry.redirectStart,
      redirectEnd: navigationEntry.redirectEnd,
      fetchStart: navigationEntry.fetchStart,
      domainLookupStart: dnsTiming.start,
      domainLookupEnd: dnsTiming.end,
      connectStart: tcpTiming.start,
      connectEnd: tcpTiming.end,
      requestStart: navigationEntry.requestStart,
      responseStart: navigationEntry.responseStart,
      responseEnd: navigationEntry.responseEnd,
      unloadEventStart: navigationEntry.unloadEventStart,
      unloadEventEnd: navigationEntry.unloadEventEnd,
      domLoading: legacyDomLoading || navigationEntry.responseEnd,
      domInteractive: navigationEntry.domInteractive,
      domContentLoadedEventEnd: navigationEntry.domContentLoadedEventEnd,
      loadEventEnd: navigationEntry.loadEventEnd,
    };
  }

  if (!legacyNavigationStart) {
    return null;
  }

  return {
    timestamp: legacyNavigationStart,
    redirectStart: getLegacyRelativeTime(legacyTiming.redirectStart, legacyNavigationStart),
    redirectEnd: getLegacyRelativeTime(legacyTiming.redirectEnd, legacyNavigationStart),
    fetchStart: getLegacyRelativeTime(legacyTiming.fetchStart, legacyNavigationStart),
    domainLookupStart: getLegacyRelativeTime(legacyTiming.domainLookupStart, legacyNavigationStart),
    domainLookupEnd: getLegacyRelativeTime(legacyTiming.domainLookupEnd, legacyNavigationStart),
    connectStart: getLegacyRelativeTime(legacyTiming.connectStart, legacyNavigationStart),
    connectEnd: getLegacyRelativeTime(legacyTiming.connectEnd, legacyNavigationStart),
    requestStart: getLegacyRelativeTime(legacyTiming.requestStart, legacyNavigationStart),
    responseStart: getLegacyRelativeTime(legacyTiming.responseStart, legacyNavigationStart),
    responseEnd: getLegacyRelativeTime(legacyTiming.responseEnd, legacyNavigationStart),
    unloadEventStart: getLegacyRelativeTime(legacyTiming.unloadEventStart, legacyNavigationStart),
    unloadEventEnd: getLegacyRelativeTime(legacyTiming.unloadEventEnd, legacyNavigationStart),
    domLoading: getLegacyRelativeTime(legacyTiming.domLoading, legacyNavigationStart),
    domInteractive: getLegacyRelativeTime(legacyTiming.domInteractive, legacyNavigationStart),
    domContentLoadedEventEnd: getLegacyRelativeTime(
      legacyTiming.domContentLoadedEventEnd,
      legacyNavigationStart,
    ),
    loadEventEnd: getLegacyRelativeTime(legacyTiming.loadEventEnd, legacyNavigationStart),
  };
}

/**
 * 读取 Paint Timing 指标；不支持时返回 0 交由上层采用兼容口径。
 */
function getWebApmPaintTiming(name: 'first-paint' | 'first-contentful-paint'): number {
  const paintEntry = performance.getEntriesByName?.(name, 'paint')[0];
  return normalizeWebApmDuration(paintEntry?.startTime ?? 0);
}

/**
 * 移除 URL 查询参数和 hash，避免将签名或业务敏感参数带入监控平台。
 */
function normalizeWebApmUrl(url: string): string {
  try {
    const normalizedUrl = new URL(url, window.location.origin);
    return `${normalizedUrl.origin}${normalizedUrl.pathname}`;
  } catch {
    return url.split(/[?#]/, 1)[0];
  }
}

/**
 * 从现有 gid Cookie 或 MT 埋点 SDK 中读取设备标识，不主动创建新的身份。
 */
function getWebApmDeviceId(): string {
  try {
    const cookieValue = document.cookie
      .split('; ')
      .find((item) => item.startsWith('gid='))
      ?.slice('gid='.length);
    if (cookieValue) {
      return decodeURIComponent(cookieValue);
    }

    const mtstat = window.mtstat as typeof window.mtstat & {
      getStatsMarker?: () => string;
    };
    return mtstat?.getStatsMarker?.() || '';
  } catch {
    return '';
  }
}

/**
 * 采集浏览器明确提供了 HTTP 状态码的资源请求，未知状态不猜测为成功。
 */
function createWebApmActions(navigationTimestamp: number): JsonObject[] {
  const resourceEntries = performance.getEntriesByType?.('resource') as ExtendedPerformanceResourceTiming[];
  if (!resourceEntries?.length) {
    return [];
  }

  return resourceEntries.flatMap((entry) => {
    if (!/^https?:\/\//.test(entry.name)
      || /https:\/\/(?:pre-)?stat\.meitudata\.com\/apm\/h5_stat/.test(entry.name)) {
      return [];
    }

    const httpCode = Number(entry.responseStatus ?? 0);
    if (!Number.isInteger(httpCode) || httpCode <= 0) {
      return [];
    }

    const method = ['fetch', 'xmlhttprequest', 'beacon'].includes(entry.initiatorType)
      ? 'UNKNOWN'
      : 'GET';
    return [{
      url: normalizeWebApmUrl(entry.name),
      timestamp: Math.round(navigationTimestamp + entry.startTime),
      httpCode,
      method,
      protocol: entry.nextHopProtocol || '',
      requestTime: getWebApmDuration(entry.responseEnd, entry.fetchStart),
      contentType: entry.contentType || '',
      initiatorType: entry.initiatorType || '',
    }];
  });
}

/**
 * 按 Hubble 约定生成页面性能字段，FMP/FMI 暂采用 LCP 与应用首次可执行时刻的近似口径。
 */
export function createWebApmPerformanceBody(
  options: WebApmPerformanceOptions = {},
): JsonObject | null {
  const navigationTiming = getWebApmNavigationTiming();
  if (!navigationTiming) {
    return null;
  }

  const firstContentfulPaint = getWebApmPaintTiming('first-contentful-paint');
  const firstPaint = getWebApmPaintTiming('first-paint') || firstContentfulPaint;
  const domLoading = navigationTiming.domLoading || navigationTiming.responseEnd;
  const domready = normalizeWebApmDuration(navigationTiming.domContentLoadedEventEnd);
  const onload = normalizeWebApmDuration(navigationTiming.loadEventEnd);
  const fmp = normalizeWebApmDuration(options.fmp ?? 0)
    || firstContentfulPaint
    || firstPaint
    || domready;
  const fmi = Math.max(
    normalizeWebApmDuration(options.fmi ?? 0),
    normalizeWebApmDuration(navigationTiming.domInteractive),
    domready,
  );
  const pageUrl = normalizeWebApmUrl(window.location.href);
  const errors = (options.errors ?? []).map((error) => ({
    ...error,
    ...(typeof error.url === 'string' ? { url: normalizeWebApmUrl(error.url) } : {}),
  }));

  return {
    pageData: {
      redirect: getWebApmDuration(navigationTiming.redirectEnd, navigationTiming.redirectStart),
      dns: getWebApmDuration(navigationTiming.domainLookupEnd, navigationTiming.domainLookupStart),
      tcp: getWebApmDuration(navigationTiming.connectEnd, navigationTiming.connectStart),
      network: getWebApmDuration(navigationTiming.requestStart, navigationTiming.fetchStart),
      send: getWebApmDuration(navigationTiming.responseStart, navigationTiming.requestStart),
      receive: getWebApmDuration(navigationTiming.responseEnd, navigationTiming.responseStart),
      backend: getWebApmDuration(navigationTiming.responseEnd, navigationTiming.requestStart),
      unload: getWebApmDuration(navigationTiming.unloadEventEnd, navigationTiming.unloadEventStart),
      dom: getWebApmDuration(navigationTiming.domContentLoadedEventEnd, domLoading),
      domready,
      load: getWebApmDuration(navigationTiming.loadEventEnd, domLoading),
      onload,
      firstPaint,
      fmp,
      fmi,
      all: Math.max(onload, domready, fmp, fmi),
      url: pageUrl,
      timestamp: navigationTiming.timestamp,
      ttfb: getWebApmDuration(navigationTiming.responseStart, navigationTiming.fetchStart),
    },
    env: {
      gid: getWebApmDeviceId(),
      sdkVersion: WEB_APM_SDK_VERSION,
      language: window.navigator.language?.toLowerCase() || '',
      ua: window.navigator.userAgent || '',
      resolution: `${window.screen.width}x${window.screen.height}`,
    },
    actions: createWebApmActions(navigationTiming.timestamp),
    errors,
  };
}

/**
 * 只在已配置 AppKey 的 PRE 与线上构建中启用 H5 APM 上报。
 */
export function resolveWebApmConfig(): WebApmRuntimeConfig | null {
  const environment = process.env.NEXT_PUBLIC_ENV;
  if (environment !== 'pre' && environment !== 'release') {
    return null;
  }

  const appKey = process.env.NEXT_PUBLIC_WEB_APM_APP_KEY?.trim() ?? '';

  if (!/^[0-9a-fA-F]{16}$/.test(appKey)) {
    warnWebApmFailure('配置不完整或格式不正确，已跳过初始化');
    return null;
  }

  return {
    appKey,
    environment,
  };
}

/**
 * 检查 H5 上报能力；SDK chunk 仍包含 BigInt 语法，旧浏览器不可加载。
 */
export function supportsWebApmRuntime(): boolean {
  return typeof window !== 'undefined'
    && typeof globalThis.BigInt === 'function'
    && typeof globalThis.fetch === 'function'
    && typeof globalThis.Headers === 'function'
    && typeof globalThis.TextEncoder === 'function'
    && typeof globalThis.TextDecoder === 'function';
}

/**
 * 生成在同一浏览器会话中保持稳定的公共客户端维度。
 */
function createWebApmClientInfo(environment: ReportingEnvironment): JsonObject {
  return {
    appName: WEB_APM_APP_NAME,
    deviceId: getWebApmDeviceId(),
    environment,
    language: window.navigator.language || '',
    platform: window.navigator.platform || '',
    userAgent: window.navigator.userAgent || '',
  };
}

/**
 * 动态加载 Web APM SDK 并完成首次单例初始化，确保 SSR 和旧浏览器安全降级。
 */
async function createWebApmReporter(): Promise<WebApmReporter | null> {
  const config = resolveWebApmConfig();
  if (!config || !supportsWebApmRuntime()) {
    return null;
  }

  try {
    const { ApmReporter } = await import('@meitu/hubble-channel');
    reporter = ApmReporter.getInstance({
      ...config,
      clientInfo: createWebApmClientInfo(config.environment),
      flushInterval: 0,
      maxBufferSize: 1,
      protocolType: 'h5',
    });
    return reporter;
  } catch (error) {
    reporterPromise = null;
    warnWebApmFailure('初始化失败，已降级为不影响业务', error);
    return null;
  }
}

/**
 * 返回全局唯一的 Web APM 初始化任务，避免多路由或 React 重渲染重复创建实例。
 */
export function initWebApm(): Promise<WebApmReporter | null> {
  if (!reporterPromise) {
    reporterPromise = createWebApmReporter();
  }
  return reporterPromise;
}

/**
 * 立即发送一条 Hubble H5 数据，统一时间字段、keepalive 与异常降级策略。
 */
async function reportWebApmBody(bodyInfo: JsonObject, failureMessage: string): Promise<void> {
  const currentReporter = await initWebApm();
  if (!currentReporter) {
    return;
  }

  const timestamp = String(Date.now());
  const deviceId = getWebApmDeviceId();
  try {
    await currentReporter.reportNow(
      {
        type: WEB_APM_REPORT_TYPE,
        time: timestamp,
        serverTime: timestamp,
        bodyInfo,
      },
      deviceId ? { deviceId } : undefined,
      { keepalive: true },
    );
  } catch (error) {
    warnWebApmFailure(failureMessage, error);
  }
}

/**
 * 按 Hubble 页面性能结构上报当前文档的导航、首屏、环境与可用资源请求数据。
 */
export async function reportWebApmPerformance(
  options: WebApmPerformanceOptions = {},
): Promise<void> {
  const bodyInfo = createWebApmPerformanceBody(options);
  if (!bodyInfo) {
    return;
  }

  await reportWebApmBody(bodyInfo, '页面性能数据上报失败');
}

/**
 * 页面转入后台或退出时尽力刷出缓冲数据，不触发新的 SDK 初始化。
 */
export async function flushWebApm(): Promise<void> {
  const currentReporter = reporter ?? (reporterPromise ? await reporterPromise : null);
  if (!currentReporter) {
    return;
  }

  try {
    await currentReporter.flush();
  } catch (error) {
    warnWebApmFailure('缓冲数据刷出失败', error);
  }
}

/**
 * 在应用生命周期结束时先刷出缓冲，再停止定时器并释放 SDK 单例。
 */
export async function shutdownWebApm(): Promise<void> {
  const currentReporter = reporter ?? (reporterPromise ? await reporterPromise : null);
  reporter = null;
  reporterPromise = null;
  if (!currentReporter) {
    return;
  }

  try {
    await currentReporter.flush();
  } catch (error) {
    warnWebApmFailure('关闭前刷出缓冲失败', error);
  }

  try {
    currentReporter.stop();
  } catch (error) {
    warnWebApmFailure('SDK 停止失败', error);
  }
}
